import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sun, Moon, Send, Loader2, User, Mic, MicOff, LogOut, Lock } from 'lucide-react';
import ChartRenderer from './components/ChartRenderer';
import Sidebar from './components/Sidebar';
import ErmeneLoader from './components/ErmeneLoader';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import AdminUsers from './pages/AdminUsers';
import BreakVerification from './pages/BreakVerification';
import ValidationDashboard from './pages/ValidationDashboard';
import SourceCalibration from './pages/SourceCalibration';
import './App.css';

const API = "/api";

const speechSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

// Rotating placeholder — 25 lingue, generico "Scrivi qui..."
const INPUT_PLACEHOLDERS = [
  'Scrivi qui…',                    // Italiano
  'Write here…',                    // English
  'Escribe aquí…',                  // Español
  'Écrivez ici…',                   // Français
  'Hier schreiben…',                // Deutsch
  'Schrijf hier…',                  // Nederlands
  'Skriv her…',                     // Dansk
  'Skriv här…',                     // Svenska
  'Напишите здесь…',                // Русский
  'Napisz tutaj…',                  // Polski
  'Napište zde…',                   // Čeština
  '在这里输入…',                      // 简体中文
  '在這裡輸入…',                      // 繁體中文
  'ここに書く…',                      // 日本語
  '여기에 쓰기…',                     // 한국어
  'यहाँ लिखें…',                     // हिन्दी
  'اكتب هنا…',                      // العربية
  'اینجا بنویسید…',                 // فارسی
  'Buraya yazın…',                  // Türkçe
  'Γράψτε εδώ…',                    // Ελληνικά
  'כתוב כאן…',                      // עברית
  'Tulis di sini…',                 // Bahasa Indonesia
  'เขียนที่นี่…',                     // ไทย
  'Skriv her…',                     // Norsk
  'Kirjoita tähän…',                // Suomi
];

// Language detection for input placeholder — returns an index in
// INPUT_PLACEHOLDERS. Fallback is 0 (Italian) for inconclusive text.
function detectLanguageIndex(text) {
  try {
    const t = (text || '').trim();
    if (!t) return 0;

    // Script-based detection (unambiguous, covers 19/25 languages)
    if (/[\u0E00-\u0E7F]/.test(t)) return 22;  // Thai
    if (/[\u0900-\u097F]/.test(t)) return 15;  // Devanagari -> Hindi
    if (/[\u0370-\u03FF]/.test(t)) return 19;  // Greek
    if (/[\u0590-\u05FF]/.test(t)) return 20;  // Hebrew
    if (/[\uAC00-\uD7AF]/.test(t)) return 14;  // Hangul -> Korean
    if (/[\u3040-\u30FF]/.test(t)) return 13;  // Kana -> Japanese
    if (/[\u0400-\u04FF]/.test(t)) return 8;   // Cyrillic -> Russian
    if (/[\u0600-\u06FF]/.test(t)) {
      return /[\u067E\u0686\u0698\u06AF]/.test(t) ? 17 : 16; // Persian vs Arabic
    }
    if (/[\u4E00-\u9FFF]/.test(t)) return 11;  // Han -> Chinese (simplified default)

    // Latin script: unique-diacritic signals (original casing for letter checks)
    if (/[ıİ]/.test(t)) return 18;             // Turkish dotless i
    if (/œ/i.test(t)) return 3;                // French œ
    if (/ñ/i.test(t)) return 2;                // Spanish ñ
    if (/ß/.test(t)) return 4;                 // German ß
    if (/[ąęłżźćń]/i.test(t)) return 9;        // Polish
    if (/[řůěšč]/i.test(t)) return 10;         // Czech (š/č also identify CZ within our 25-lang set)
    if (/[æø]/i.test(t)) return 6;             // Dansk/Norsk (Swedish doesn't use these)

    // Stopword-based detection on token set.
    // NOTE: Scandinavian stopwords are checked BEFORE the Swedish `å` rule
    // because `å` is shared between Swedish and Norwegian.
    const tokens = new Set((t.toLowerCase().match(/\p{L}+/gu)) || []);
    const any = (arr) => arr.some(w => tokens.has(w));

    if (any(['le','la','les','un','une','des','est','qui','pour','avec','comment','pourquoi','quand','très'])) return 3;   // French
    if (any(['der','die','das','ist','und','nicht','ein','eine','mit','für','wie','was','wo','wann','auch'])) return 4;    // German
    if (any(['het','een','van','voor','niet','wat','hoe','waar','wanneer','met','maar','zijn','hebben'])) return 5;        // Dutch
    if (any(['qué','cómo','dónde','cuándo','por','para','con','los','las','esta','esto','está','pero','hola','quién'])) return 2;  // Spanish
    if (any(['che','come','dove','quando','perché','per','con','il','lo','gli','una','questa','questo','ciao','grazie','però','anche','molto'])) return 0;  // Italian
    if (any(['yang','dan','ini','itu','tidak','apa','bagaimana','dengan','untuk','adalah','bisa'])) return 21;             // Indonesian
    if (any(['hva','hvor','hvordan','hvad','hvilken','ikke','jeg'])) return 6;                                              // Dansk/Norsk (before å rule)
    if (any(['hur','vad','när','varför','är','och','inte','jag'])) return 7;                                                // Swedish (before å rule)
    if (/å/i.test(t)) return 7;                                                                                             // Svenska fallback diacritic
    if (any(['jest','nie','tak','czy','jak','gdzie','kiedy','dlaczego'])) return 9;                                         // Polish (fallback)
    if (any(['je','ne','ano','se','kde','kdy','proč','jsem','jsme','má','máš'])) return 10;                                 // Czech (fallback)
    if (any(['on','ei','että','mutta','kuin','kun','ole','mitä','miten','missä','milloin'])) return 24;                     // Finnish
    if (any(['og','er','det','hvad','hvordan'])) return 6;                                                                  // Dansk/Norsk (secondary fallback)
    if (any(['bir','için','ile','değil','yok','bu','şu','nasıl','nerede'])) return 18;                                      // Turkish (fallback)
    if (any(['the','and','what','how','is','are','you','to','of','in','for','with','this','that','where','when','why'])) return 1;  // English

    return 0; // fallback Italian
  } catch {
    return 0;
  }
}

// Welcome messages — 57 lingue, frase invito completa
// Global reach showcase: EU + Nordic + Slavic + East Asia + SE Asia + S Asia + Middle East + Africa + Americas + Latin/Esperanto
const WELCOME_MESSAGES = [
  // === EUROPA OCCIDENTALE ===
  { code: "IT",     flag: "🇮🇹", langAttr: "it",    text: "Scrivi sotto una domanda come \"Confronta il calendario ideale di Sinner e Alcaraz\" ma sentiti libero di giocare" },
  { code: "EN",     flag: "🇬🇧", langAttr: "en",    text: "Write below a question like \"Compare the ideal calendar of Sinner and Alcaraz\" but feel free to play" },
  { code: "ES",     flag: "🇪🇸", langAttr: "es",    text: "Escribe abajo una pregunta como \"Compara el calendario ideal de Sinner y Alcaraz\" pero siéntete libre de jugar" },
  { code: "FR",     flag: "🇫🇷", langAttr: "fr",    text: "Écrivez ci-dessous une question comme \"Comparez le calendrier idéal de Sinner et Alcaraz\" mais n'hésitez pas à jouer" },
  { code: "DE",     flag: "🇩🇪", langAttr: "de",    text: "Schreiben Sie unten eine Frage wie \"Vergleiche den idealen Kalender von Sinner und Alcaraz\" aber spielen Sie ruhig" },
  { code: "PT",     flag: "🇵🇹", langAttr: "pt",    text: "Escreva abaixo uma pergunta como \"Compare o calendário ideal de Sinner e Alcaraz\" mas sinta-se livre para brincar" },
  { code: "NL",     flag: "🇳🇱", langAttr: "nl",    text: "Schrijf hieronder een vraag zoals \"Vergelijk de ideale kalender van Sinner en Alcaraz\" maar voel je vrij om te spelen" },
  { code: "NL-BE",  flag: "🇧🇪", langAttr: "nl-BE", text: "Schrijf hieronder een vraag zoals \"Vergelijk de ideale agenda van Sinner en Alcaraz\" maar voel je vrij om te spelen" },
  { code: "GA",     flag: "🇮🇪", langAttr: "ga",    text: "Scríobh thíos ceist mar \"Déan comparáid idir féilire idéalach Sinner agus Alcaraz\" ach bíodh saoirse agat imirt" },

  // === EUROPA NORDICA ===
  { code: "DA",     flag: "🇩🇰", langAttr: "da",    text: "Skriv nedenfor et spørgsmål som \"Sammenlign den ideelle kalender for Sinner og Alcaraz\" men føl dig fri til at lege" },
  { code: "SV",     flag: "🇸🇪", langAttr: "sv",    text: "Skriv nedan en fråga som \"Jämför den ideala kalendern för Sinner och Alcaraz\" men känn dig fri att leka" },
  { code: "NO",     flag: "🇳🇴", langAttr: "no",    text: "Skriv nedenfor et spørsmål som \"Sammenlign den ideelle kalenderen til Sinner og Alcaraz\" men føl deg fri til å leke" },
  { code: "FI",     flag: "🇫🇮", langAttr: "fi",    text: "Kirjoita alle kysymys kuten \"Vertaa Sinnerin ja Alcarazin ihanteellista kalenteria\" mutta voit leikkiä vapaasti" },
  { code: "IS",     flag: "🇮🇸", langAttr: "is",    text: "Skrifaðu hér að neðan spurningu eins og \"Berðu saman tilvalda dagskrá Sinner og Alcaraz\" en þú mátt leika þér" },

  // === EUROPA ORIENTALE ===
  { code: "RU",     flag: "🇷🇺", langAttr: "ru",    text: "Напишите ниже вопрос типа \"Сравните идеальный календарь Синнера и Алькараса\" но не стесняйтесь играть" },
  { code: "PL",     flag: "🇵🇱", langAttr: "pl",    text: "Napisz poniżej pytanie takie jak \"Porównaj idealny kalendarz Sinnera i Alcaraza\" ale możesz się bawić" },
  { code: "CS",     flag: "🇨🇿", langAttr: "cs",    text: "Napište níže otázku jako \"Porovnejte ideální kalendář Sinnera a Alcaraze\" ale klidně si hrajte" },
  { code: "SK",     flag: "🇸🇰", langAttr: "sk",    text: "Napíšte nižšie otázku ako \"Porovnajte ideálny kalendár Sinnera a Alcaraza\" ale pokojne sa hrajte" },
  { code: "UK",     flag: "🇺🇦", langAttr: "uk",    text: "Напишіть нижче питання на кшталт \"Порівняйте ідеальний календар Сіннера та Алькараса\" але не соромтеся грати" },
  { code: "RO",     flag: "🇷🇴", langAttr: "ro",    text: "Scrie mai jos o întrebare precum \"Compară calendarul ideal al lui Sinner și Alcaraz\" dar simte-te liber să te joci" },
  { code: "HU",     flag: "🇭🇺", langAttr: "hu",    text: "Írj alul egy kérdést, mint \"Hasonlítsd össze Sinner és Alcaraz ideális naptárát\" de nyugodtan játssz" },
  { code: "BG",     flag: "🇧🇬", langAttr: "bg",    text: "Напишете по-долу въпрос като \"Сравнете идеалния календар на Синър и Алкарас\" но се чувствайте свободни да играете" },
  { code: "HR",     flag: "🇭🇷", langAttr: "hr",    text: "Napišite ispod pitanje poput \"Usporedite idealan kalendar Sinnera i Alcaraza\" ali slobodno se igrajte" },
  { code: "SR",     flag: "🇷🇸", langAttr: "sr",    text: "Напишите испод питање попут \"Упоредите идеалан календар Синера и Алкараза\" али слободно се играјте" },
  { code: "SL",     flag: "🇸🇮", langAttr: "sl",    text: "Spodaj napišite vprašanje, kot je \"Primerjaj idealen koledar Sinnerja in Alcaraza\" a se lahko igrate" },
  { code: "LT",     flag: "🇱🇹", langAttr: "lt",    text: "Parašykite žemiau klausimą kaip \"Palygink idealų Sinner ir Alcaraz kalendorių\" bet laisvai žaiskite" },
  { code: "LV",     flag: "🇱🇻", langAttr: "lv",    text: "Rakstiet zemāk jautājumu kā \"Salīdziniet ideālo Sinnera un Alcaraza kalendāru\" bet jūtieties brīvi spēlēties" },
  { code: "ET",     flag: "🇪🇪", langAttr: "et",    text: "Kirjutage allpool küsimus nagu \"Võrrelge Sinneri ja Alcarazi ideaalset kalendrit\" aga tundke end vabalt mängima" },

  // === ASIA ORIENTALE ===
  { code: "ZH",     flag: "🇨🇳", langAttr: "zh",    text: "在下方写一个问题，比如\"比较辛纳和阿尔卡拉斯的理想赛程\"，但你可以自由发挥" },
  { code: "ZH-TW",  flag: "🇹🇼", langAttr: "zh-TW", text: "在下方寫一個問題，比如\"比較辛納和阿爾卡拉斯的理想賽程\"，但你可以自由發揮" },
  { code: "JA",     flag: "🇯🇵", langAttr: "ja",    text: "下に「シナーとアルカラスの理想的なカレンダーを比較して」のような質問を書いてください、自由に遊んでください" },
  { code: "KO",     flag: "🇰🇷", langAttr: "ko",    text: "아래에 \"시너와 알카라스의 이상적인 일정을 비교해줘\"와 같은 질문을 작성하세요, 자유롭게 즐기세요" },
  { code: "MN",     flag: "🇲🇳", langAttr: "mn",    text: "Доор \"Синнер болон Алькаразын төгс хуваарийг харьцуулна уу\" гэх мэт асуулт бичээрэй, чөлөөтэй тоглоорой" },

  // === ASIA SUD-EST ===
  { code: "ID",     flag: "🇮🇩", langAttr: "id",    text: "Tulis di bawah pertanyaan seperti \"Bandingkan jadwal ideal Sinner dan Alcaraz\" tapi bebas bermain" },
  { code: "MS",     flag: "🇲🇾", langAttr: "ms",    text: "Tulis di bawah soalan seperti \"Bandingkan jadual ideal Sinner dan Alcaraz\" tetapi rasa bebas untuk bermain" },
  { code: "TH",     flag: "🇹🇭", langAttr: "th",    text: "เขียนคำถามด้านล่าง เช่น \"เปรียบเทียบตารางการแข่งขันในอุดมคติของ Sinner และ Alcaraz\" แต่คุณสามารถเล่นได้อย่างอิสระ" },
  { code: "VI",     flag: "🇻🇳", langAttr: "vi",    text: "Viết bên dưới một câu hỏi như \"So sánh lịch thi đấu lý tưởng của Sinner và Alcaraz\" nhưng hãy thoải mái chơi" },
  { code: "TL",     flag: "🇵🇭", langAttr: "tl",    text: "Magsulat sa ibaba ng tanong tulad ng \"Ihambing ang ideal na kalendaryo nina Sinner at Alcaraz\" pero malayang maglaro" },

  // === ASIA MERIDIONALE ===
  { code: "HI",     flag: "🇮🇳", langAttr: "hi",    text: "नीचे एक प्रश्न लिखें जैसे \"सिनर और अल्काराज़ के आदर्श कैलेंडर की तुलना करें\" लेकिन खेलने के लिए स्वतंत्र महसूस करें" },
  { code: "BN",     flag: "🇧🇩", langAttr: "bn",    text: "নিচে একটি প্রশ্ন লিখুন যেমন \"সিনার এবং আলকারাজের আদর্শ ক্যালেন্ডার তুলনা করুন\" কিন্তু খেলতে স্বাধীন বোধ করুন" },
  { code: "UR",     flag: "🇵🇰", langAttr: "ur", dir: "rtl", text: "نیچے ایک سوال لکھیں جیسے \"سنر اور الکاراز کے مثالی کیلنڈر کا موازنہ کریں\" لیکن کھیلنے کے لیے آزاد محسوس کریں" },
  { code: "SI",     flag: "🇱🇰", langAttr: "si",    text: "පහත \"සිනර් සහ ඇල්කාරාස්ගේ පරමාදර්ශී දින දර්ශනය සසඳන්න\" වැනි ප්‍රශ්නයක් ලියන්න, නමුත් නිදහසේ සෙල්ලම් කරන්න" },

  // === MEDIO ORIENTE ===
  { code: "AR",     flag: "🇸🇦", langAttr: "ar", dir: "rtl", text: "اكتب أدناه سؤالاً مثل \"قارن بين الجدول المثالي لسينر وألكاراز\" ولكن لا تتردد في اللعب" },
  { code: "FA",     flag: "🇮🇷", langAttr: "fa", dir: "rtl", text: "در زیر سوالی مانند \"تقویم ایده‌آل سینر و آلکاراز را مقایسه کن\" بنویسید اما آزاد باشید که بازی کنید" },
  { code: "TR",     flag: "🇹🇷", langAttr: "tr",    text: "Aşağıya \"Sinner ve Alcaraz'ın ideal takvimini karşılaştır\" gibi bir soru yazın ama oynamakta özgürsünüz" },
  { code: "HE",     flag: "🇮🇱", langAttr: "he", dir: "rtl", text: "כתבו למטה שאלה כמו \"השוו את לוח הזמנים האידיאלי של סינר ואלקאראז\" אבל הרגישו חופשיים לשחק" },

  // === MEDITERRANEO / BALCANI ===
  { code: "EL",     flag: "🇬🇷", langAttr: "el",    text: "Γράψτε παρακάτω μια ερώτηση όπως \"Συγκρίνετε το ιδανικό πρόγραμμα του Sinner και του Alcaraz\" αλλά μη διστάσετε να παίξετε" },
  { code: "SQ",     flag: "🇦🇱", langAttr: "sq",    text: "Shkruani më poshtë një pyetje si \"Krahasoni kalendarin ideal të Sinner dhe Alcaraz\" por ndjehuni të lirë të luani" },
  { code: "MT",     flag: "🇲🇹", langAttr: "mt",    text: "Ikteb hawn taħt mistoqsija bħal \"Qabbel il-kalendarju ideali ta' Sinner u Alcaraz\" imma ħossok liberu tilgħab" },

  // === AFRICA ===
  { code: "AM",     flag: "🇪🇹", langAttr: "am",    text: "ከዚህ በታች \"የሲነር እና አልካራዝን ተስማሚ የቀን መቁጠሪያ አወዳድር\" የሚል ጥያቄ ይፃፉ ነገር ግን በነፃነት ይጫወቱ" },
  { code: "SW",     flag: "🇰🇪", langAttr: "sw",    text: "Andika chini swali kama \"Linganisha kalenda bora ya Sinner na Alcaraz\" lakini jisikie huru kucheza" },
  { code: "AF",     flag: "🇿🇦", langAttr: "af",    text: "Skryf hieronder 'n vraag soos \"Vergelyk die ideale kalender van Sinner en Alcaraz\" maar voel vry om te speel" },
  { code: "ZU",     flag: "🇿🇦", langAttr: "zu",    text: "Bhala ngezansi umbuzo onjengo \"Qhathanisa ikhalenda efanele ka-Sinner no-Alcaraz\" kodwa uzizwe ukhululekile ukudlala" },

  // === AMERICHE ===
  { code: "PT-BR",  flag: "🇧🇷", langAttr: "pt-BR", text: "Escreva abaixo uma pergunta como \"Compare o calendário ideal de Sinner e Alcaraz\" mas sinta-se livre para brincar" },
  { code: "ES-MX",  flag: "🇲🇽", langAttr: "es-MX", text: "Escribe abajo una pregunta como \"Compara el calendario ideal de Sinner y Alcaraz\" pero siéntete libre de jugar" },

  // === LINGUE COSTRUITE / ACCADEMICHE ===
  { code: "LA",     flag: "🏛️", langAttr: "la",    text: "Infra scribe quaestionem sicut \"Confer calendarium ideale Sinner et Alcaraz\" sed libere ludere potes" },
  { code: "EO",     flag: "🌍", langAttr: "eo",    text: "Skribu sube demandon kiel \"Komparu la idealan kalendaron de Sinner kaj Alcaraz\" sed sentu vin libera ludi" },
];

function ExportPDFButton({ messageIndex, playerName }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      // Find assistant message in DOM
      const allMsgs = document.querySelectorAll('.message');
      const assistantMsgs = Array.from(allMsgs).filter(m => !m.classList.contains('message-user'));
      const msgEl = assistantMsgs[messageIndex];
      if (!msgEl) { setExporting(false); return; }

      // Find preceding user question
      const msgArray = Array.from(allMsgs);
      const idx = msgArray.indexOf(msgEl);
      const userMsg = msgArray.slice(0, idx).reverse().find(m => m.classList.contains('message-user'));
      const questionText = userMsg?.textContent?.trim()?.slice(0, 100);

      // Clone and clean
      const clone = msgEl.cloneNode(true);
      clone.querySelectorAll('.feedback-widget, .export-pdf-btn, button').forEach(el => el.remove());

      // Fix Recharts animations and visibility
      clone.querySelectorAll('.recharts-wrapper *').forEach(el => {
        el.style.animationDuration = '0s';
        el.style.animationDelay = '0s';
        el.style.transition = 'none';
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      });
      clone.querySelectorAll('svg *').forEach(el => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      });

      // Off-screen wrapper at natural width
      const naturalWidth = Math.max(msgEl.offsetWidth, 680);
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `width:${naturalWidth}px;background:#0E0C1E;padding:0;position:fixed;top:-9999px;left:-9999px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;`;

      // HTML header with logo + question
      const header = document.createElement('div');
      header.style.cssText = 'background:#0E0C1E;padding:20px 28px 16px 28px;border-bottom:1px solid rgba(174,169,236,0.25);margin-bottom:0;';
      header.innerHTML = `<div style="display:flex;align-items:baseline;gap:10px;margin-bottom:${questionText ? '10px' : '0'};"><span style="color:#EEEDFE;font-size:20px;font-weight:600;letter-spacing:4px;font-family:inherit;">ERMENE</span><span style="color:#AFA9EC;font-size:11px;letter-spacing:2px;font-family:inherit;">for Tennis</span></div>${questionText ? `<div style="color:#EEEDFE;font-size:14px;font-weight:500;font-family:inherit;margin-top:4px;">${questionText}</div><div style="color:rgba(174,169,236,0.5);font-size:10px;font-family:inherit;margin-top:4px;">${new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}</div>` : ''}`;

      const content = document.createElement('div');
      content.style.cssText = 'padding:20px 32px 32px 32px;';
      content.appendChild(clone);
      wrapper.appendChild(header);
      wrapper.appendChild(content);
      document.body.appendChild(wrapper);
      await new Promise(r => setTimeout(r, 800));

      const canvas = await html2canvas(wrapper, { backgroundColor: '#0E0C1E', scale: 2, useCORS: true, allowTaint: true });
      document.body.removeChild(wrapper);

      // PDF A4
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const PAGE_W = 210, PAGE_H = 297, M = 14, FOOTER_H = 10;
      const imgW = PAGE_W - M * 2;
      const imgH = (canvas.height / canvas.width) * imgW;

      const drawFooter = (p, tot) => {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(175, 169, 236);
        pdf.text(`ermene.it  \u2014  ${new Date().toLocaleDateString('it-IT')}  \u2014  ${p}/${tot}`, M, PAGE_H - 5);
      };

      // Page geometry — header is baked into canvas, all pages use same layout
      const CONTENT_H_PAGE = PAGE_H - M * 2 - FOOTER_H;
      const pxPerMm = canvas.width / imgW;
      const pageHeightPx = Math.floor(CONTENT_H_PAGE * pxPerMm);

      // Build page slices
      const pages = [];
      let yPx = 0;
      while (yPx < canvas.height) {
        const h = Math.min(pageHeightPx, canvas.height - yPx);
        if (h < 80) break;
        pages.push({ yPx, h });
        yPx += h;
      }

      const totalPages = pages.length;
      pages.forEach(({ yPx: sliceY, h }, i) => {
        if (i > 0) pdf.addPage();

        const sc = document.createElement('canvas');
        sc.width = canvas.width;
        sc.height = h;
        const ctx = sc.getContext('2d');
        ctx.fillStyle = '#0E0C1E';
        ctx.fillRect(0, 0, sc.width, sc.height);
        ctx.drawImage(canvas, 0, -sliceY);

        const sliceH_mm = (h / canvas.width) * imgW;
        pdf.addImage(sc.toDataURL('image/png'), 'PNG', M, M, imgW, sliceH_mm);
        drawFooter(i + 1, totalPages);
      });

      const slug = playerName ? playerName.split(' ').pop().toLowerCase() : 'report';
      pdf.save(`ermene_${slug}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('PDF:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <button className="export-pdf-btn" onClick={handleExport} disabled={exporting} title="Esporta come PDF">
      {exporting ? '\u23F3' : '\u2193 PDF'}
    </button>
  );
}

function FeedbackWidget({ messageIndex, hasCharts, chartTypes, languageMode, sessionId, submitted, onSubmit, authFetch }) {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [chartHelpful, setChartHelpful] = useState(null);
  const [notes, setNotes] = useState('');

  if (submitted) return <div className="feedback-done">Grazie per il feedback</div>;

  const handleStarClick = (n) => { setSelected(n); setExpanded(true); };

  const handleSubmit = async () => {
    try {
      await authFetch(`${API}/feedback`, {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId, message_index: messageIndex,
          quality_score: selected, chart_helpful: chartHelpful,
          chart_types: JSON.stringify(chartTypes), language_mode: languageMode,
          notes: notes || null,
        }),
      });
      onSubmit();
    } catch (e) {}
  };

  return (
    <div className="feedback-widget">
      <div className="feedback-stars">
        {[1,2,3,4,5].map(n => (
          <span key={n} className={`feedback-star ${n <= (hovered || selected) ? 'active' : ''}`}
            onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(0)}
            onClick={() => handleStarClick(n)}>&#9733;</span>
        ))}
        {!expanded && <span className="feedback-label">Valuta la risposta</span>}
      </div>
      {expanded && (
        <div className="feedback-expanded">
          {hasCharts && (
            <div className="feedback-chart-q">
              Il grafico ha aiutato?
              <label><input type="radio" name={`chart-${messageIndex}`} onChange={() => setChartHelpful('yes')} /> Si</label>
              <label><input type="radio" name={`chart-${messageIndex}`} onChange={() => setChartHelpful('no')} /> No</label>
            </div>
          )}
          <textarea className="feedback-notes" placeholder="Note opzionali..." rows={2}
            value={notes} onChange={e => setNotes(e.target.value)} />
          <button className="feedback-submit" onClick={handleSubmit} disabled={selected === 0}>Invia</button>
        </div>
      )}
    </div>
  );
}

function ChatView() {
  const { user, token, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [streamingText, setStreamingText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [listening, setListening] = useState(false);
  const [languageMode, setLanguageMode] = useState('standard');
  const [feedbacks, setFeedbacks] = useState({});
  const messagesEndRef = useRef(null);
  const lastAssistantRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const streamBufferRef = useRef('');
  const streamFlushTimerRef = useRef(null);
  const streamWarmupTimerRef = useRef(null);
  const streamChunkCountRef = useRef(0);
  const streamInitialReleasedRef = useRef(false);

  // Warmup: tieni il testo invisibile per ~800ms OR 6 chunk
  // (whichever first) → evita il "flash" di 'C', 'Ci', 'Cia...'
  // Post-warmup: flush debounced 500ms con coalesce (no re-arm)
  // Word-boundary hold: durante i flush intermedi non mostriamo
  // la parola parziale finale — resta nel buffer per il prossimo
  // flush. Sul `done` facciamo flush FULL (niente holdback).
  // Streaming cadence — sentence-boundary flush con warmup 1500ms.
  // Preserva real streaming (chunks SSE continuano ad arrivare in
  // real-time) ma rivela testo a frasi complete invece che word-by-word.
  const INITIAL_CHUNK_BUFFER = 999;    // disabilitato: solo time-based trigger
  const INITIAL_WARMUP_MS = 1500;      // "pensiero" indicator visibile 1.5s
  const FLUSH_DEBOUNCE_MS = 150;       // tick veloce tra flush sentence-by-sentence
  const MAX_HOLD_WITHOUT_SENTENCE = 500; // chars: se no sentence end, flush a word-boundary

  const flushSafe = () => {
    // Release buffer fino all'ultimo sentence boundary (. ! ?).
    // Se buffer troppo lungo senza sentence end, fallback word-boundary
    // per non tenere l'utente in attesa indefinita.
    const buf = streamBufferRef.current;
    // Match greedy fino a .!? seguito da \s o end-of-string
    const sentence = buf.match(/^([\s\S]*[.!?])(?:\s+|$)/);
    if (sentence) {
      // Include tutto fino al punto + spazio successivo se presente
      const endIdx = sentence[0].length;
      setStreamingText(buf.substring(0, endIdx));
      return;
    }
    // Fallback: buffer troppo lungo senza punctuazione → word-boundary release
    if (buf.length > MAX_HOLD_WITHOUT_SENTENCE) {
      const word = buf.match(/^([\s\S]*\s)\S*$/);
      setStreamingText(word ? word[1] : buf);
    }
    // else: silenzio, aspetta prossima frase
  };
  const flushStreamFinal = () => {
    // Release completo (usato su `done` event)
    if (streamFlushTimerRef.current) {
      clearTimeout(streamFlushTimerRef.current);
      streamFlushTimerRef.current = null;
    }
    if (streamWarmupTimerRef.current) {
      clearTimeout(streamWarmupTimerRef.current);
      streamWarmupTimerRef.current = null;
    }
    setStreamingText(streamBufferRef.current);
    streamInitialReleasedRef.current = true;
  };
  const releaseInitialNow = () => {
    if (streamInitialReleasedRef.current) return;
    streamInitialReleasedRef.current = true;
    if (streamWarmupTimerRef.current) {
      clearTimeout(streamWarmupTimerRef.current);
      streamWarmupTimerRef.current = null;
    }
    flushSafe();
  };
  const scheduleStreamFlush = () => {
    if (streamFlushTimerRef.current) return; // coalesce, no re-arm
    streamFlushTimerRef.current = setTimeout(() => {
      streamFlushTimerRef.current = null;
      flushSafe();
    }, FLUSH_DEBOUNCE_MS);
  };
  const startStreamWarmup = () => {
    streamWarmupTimerRef.current = setTimeout(() => {
      streamWarmupTimerRef.current = null;
      if (!streamInitialReleasedRef.current && streamBufferRef.current.length > 0) {
        releaseInitialNow();
      }
    }, INITIAL_WARMUP_MS);
  };
  const resetStreamBuffer = () => {
    if (streamFlushTimerRef.current) {
      clearTimeout(streamFlushTimerRef.current);
      streamFlushTimerRef.current = null;
    }
    if (streamWarmupTimerRef.current) {
      clearTimeout(streamWarmupTimerRef.current);
      streamWarmupTimerRef.current = null;
    }
    streamBufferRef.current = '';
    streamChunkCountRef.current = 0;
    streamInitialReleasedRef.current = false;
    setStreamingText('');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Placeholder rotation — solo quando input vuoto + non streaming
  useEffect(() => {
    if (messages.length > 0) return; // solo su welcome screen
    const id = setInterval(() => {
      setPlaceholderIdx(i => (i + 1) % INPUT_PLACEHOLDERS.length);
    }, 3000);
    return () => clearInterval(id);
  }, [messages.length]);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role === 'assistant') {
      // Scroll to start of the new assistant message
      setTimeout(() => {
        lastAssistantRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      // User message — scroll to bottom so they see the loading indicator
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!currentPlayer) { setPlayerProfile(null); return; }
    const [name, tourPart] = currentPlayer.split(' (');
    const tour = tourPart ? tourPart.replace(')', '') : 'ATP';
    fetch(`${API}/player/${encodeURIComponent(name)}/profile?tour=${tour}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setPlayerProfile(d); })
      .catch(() => {});
  }, [currentPlayer]);

  // Sync fallback — POST /chat, wait for full JSON (used if streaming fails)
  const sendSync = async (userMsg) => {
    const res = await authFetch(`${API}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message: userMsg, session_id: sessionId, language_mode: languageMode }),
    });
    if (res.status === 401) { logout(); return; }
    if (res.status === 503) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'I server sono temporaneamente sovraccarichi. Attendi qualche secondo e riprova.',
        charts: [], error: true,
      }]);
      return;
    }
    const data = await res.json();
    setSessionId(data.session_id);
    if (data.player) setCurrentPlayer(data.player);
    setMessages(prev => [...prev, {
      role: 'assistant',
      text: data.response,
      charts: data.charts || [],
      meta: data.meta,
    }]);
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    if (messages.length === 0) {
      setPlaceholderIdx(detectLanguageIndex(userMsg));
    }
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    resetStreamBuffer();
    startStreamWarmup();

    let streamSucceeded = false;

    try {
      const res = await authFetch(`${API}/chat-stream`, {
        method: 'POST',
        body: JSON.stringify({ message: userMsg, session_id: sessionId, language_mode: languageMode }),
      });
      if (res.status === 401) { logout(); return; }
      if (!res.ok || !res.body) {
        throw new Error(`stream http ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;
      let accumulatedChunks = '';
      let doneData = null;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line (\n\n)
        let sepIdx;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          let evt = null;
          let dataRaw = null;
          for (const line of frame.split('\n')) {
            if (line.startsWith('event: ')) evt = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataRaw = line.slice(6);
          }
          if (!evt || dataRaw == null) continue;
          let data;
          try { data = JSON.parse(dataRaw); } catch { continue; }

          if (evt === 'chunk') {
            const text = data.text || '';
            accumulatedChunks += text;
            streamBufferRef.current += text;
            streamChunkCountRef.current += 1;
            if (!streamInitialReleasedRef.current) {
              // Warmup: silent accumulation finché non scatta
              // il contatore chunk OR il timer ms (startStreamWarmup)
              if (streamChunkCountRef.current >= INITIAL_CHUNK_BUFFER) {
                releaseInitialNow();
              }
            } else {
              scheduleStreamFlush();
            }
          } else if (evt === 'tool_start') {
            // Loader is purely graphical now — no tool name displayed.
          } else if (evt === 'done') {
            doneData = data;
          } else if (evt === 'error') {
            streamError = data.message || 'stream error';
          }
          currentEvent = evt;
        }
      }

      if (streamError) throw new Error(streamError);
      if (!doneData) throw new Error('stream ended without done event');

      flushStreamFinal();

      // Apply final clean_text (tags stripped) + charts
      streamSucceeded = true;
      setSessionId(doneData.session_id);
      if (doneData.player) setCurrentPlayer(doneData.player);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: doneData.clean_text || accumulatedChunks,
        charts: doneData.charts || [],
        meta: doneData.meta || {},
      }]);
    } catch (streamErr) {
      console.warn('[Ermene] streaming failed, falling back to /chat', streamErr);
      try {
        await sendSync(userMsg);
        streamSucceeded = true;
      } catch (syncErr) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: 'Errore di connessione. Controlla che il backend sia attivo su localhost:8000.',
          charts: [], error: true,
        }]);
      }
    } finally {
      setLoading(false);
      resetStreamBuffer();
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    const recognition = new SR();
    recognition.lang = 'it-IT';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + transcript : transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <svg width="40" height="56" viewBox="0 0 100 140" style={{display:'block', flexShrink:0}}>
            <ellipse cx="50" cy="55" rx="36" ry="43" fill="none" stroke="#AFA9EC" strokeWidth="5" opacity="0.20"/>
            <ellipse cx="50" cy="55" rx="23" ry="30" fill="none" stroke="#AFA9EC" strokeWidth="3.5" opacity="0.35"/>
            <ellipse cx="50" cy="55" rx="14" ry="19" fill="none" stroke="#AFA9EC" strokeWidth="2.5" opacity="0.55"/>
            <ellipse cx="50" cy="55" rx="9" ry="11" fill="none" stroke="#CECBF6" strokeWidth="1.8" opacity="0.75"/>
            <ellipse cx="50" cy="55" rx="5" ry="7" fill="#EEEDFE"/>
            <ellipse cx="49" cy="53" rx="2" ry="2" fill="white" opacity="0.5"/>
          </svg>
          <div className="logo-wordmark">
            <span className="logo-text">ERMENE</span>
            <span className="logo-subtitle">for Tennis</span>
          </div>
        </div>
        <div className="header-right">
          {user?.username && <span className="header-subtitle">{user.username}</span>}
          {(user?.role === 'operator' || user?.role === 'admin') && (
            <a href="/admin/breaks" className="theme-toggle" style={{ textDecoration: 'none', fontSize: 12 }}>Breaks</a>
          )}
          {user?.role === 'admin' && (
            <a href="/admin/validation" className="theme-toggle" style={{ textDecoration: 'none', fontSize: 12 }}>Health</a>
          )}
          {(user?.role === 'operator' || user?.role === 'admin') && (
            <a href="/admin/calibration" className="theme-toggle" style={{ textDecoration: 'none', fontSize: 12 }}>Calibration</a>
          )}
          <div className="language-toggle">
            {['expert', 'standard', 'simple'].map(mode => (
              <button
                key={mode}
                className={`lang-btn ${languageMode === mode ? 'active' : ''}`}
                onClick={() => setLanguageMode(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <button className="sidebar-toggle-mobile" onClick={() => setSidebarOpen(o => !o)}>
            {currentPlayer ? currentPlayer.split(' (')[0].split(' ').pop() : <User size={14}/>}
          </button>
          <button className="theme-toggle" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16}/> : <Moon size={16}/>}
          </button>
          {token ? (
            <button className="theme-toggle" onClick={logout} title="Esci">
              <LogOut size={16}/>
            </button>
          ) : (
            <button className="theme-toggle" onClick={() => navigate('/login')} title="Accedi">
              <Lock size={16}/>
            </button>
          )}
        </div>
      </header>

      <div className="main">
        <div className="chat-panel">
          <div className="messages">
            {messages.length === 0 && (
              <div className="welcome-screen">
                <ul className="welcome-messages" aria-label="Write a question in any language">
                  {WELCOME_MESSAGES.map((m) => (
                    <li
                      key={m.code}
                      lang={m.langAttr}
                      dir={m.dir || 'auto'}
                      className={m.dir === 'rtl' ? 'is-rtl' : undefined}
                    >
                      <span className="msg-lang" aria-hidden="true">
                        <span className="msg-flag">{m.flag}</span>
                        <span className="msg-code">{m.code}</span>
                      </span>
                      <span className="msg-text">{m.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {messages.map((msg, i) => {
              const isLastAssistant = msg.role === 'assistant' && i === messages.map(m => m.role).lastIndexOf('assistant');
              return (
              <div key={i} ref={isLastAssistant ? lastAssistantRef : null} className={`message message-${msg.role}${msg.error ? ' message-error' : ''}`}>
                <div className="message-bubble">
                  <div className="message-text"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{table: ({node, ...props}) => (<div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 8}}><table {...props} /></div>)}}>{msg.text}</ReactMarkdown></div>
                  {msg.charts && msg.charts.map((chart, ci) => (
                    <ChartRenderer key={ci} spec={chart} player={currentPlayer} />
                  ))}
                  {msg.role === 'assistant' && !msg.error && (
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                      <FeedbackWidget
                        messageIndex={i} hasCharts={msg.charts?.length > 0}
                        chartTypes={msg.charts?.map(c => c.type) || []}
                        languageMode={languageMode} sessionId={sessionId}
                        submitted={feedbacks[i]?.submitted}
                        onSubmit={() => setFeedbacks(prev => ({...prev, [i]: {submitted: true}}))}
                        authFetch={authFetch}
                      />
                      <ExportPDFButton
                        messageIndex={messages.slice(0, i + 1).filter(m => m.role === 'assistant').length - 1}
                        playerName={currentPlayer}
                      />
                    </div>
                  )}
                </div>
              </div>
              );
            })}
            {loading && (
              <div className="message message-assistant">
                <div className="message-bubble">
                  {streamingText ? (
                    <>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {streamingText}
                      </ReactMarkdown>
                      <div className="ermene-loader-streaming-cursor">▊</div>
                    </>
                  ) : (
                    <ErmeneLoader />
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-area">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={INPUT_PLACEHOLDERS[placeholderIdx]}
              dir="auto"
              disabled={loading}
              aria-label="Ask Ermene in any language"
            />
            {speechSupported && (
              <button className={`mic-btn ${listening ? 'active' : ''}`} onClick={startVoice} title="Parla">
                {listening ? <MicOff size={18}/> : <Mic size={18}/>}
              </button>
            )}
            <button onClick={send} disabled={loading || !input.trim()} className="send-btn">
              <Send size={18} />
            </button>
          </div>
        </div>

        <Sidebar player={currentPlayer} profile={playerProfile} mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} />
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/chat" element={<ChatView />} />
          <Route path="/admin/breaks" element={<ProtectedRoute requiredRole="operator"><BreakVerification /></ProtectedRoute>} />
          <Route path="/admin/validation" element={<ProtectedRoute requiredRole="admin"><ValidationDashboard /></ProtectedRoute>} />
          <Route path="/admin/calibration" element={<ProtectedRoute requiredRole="operator"><SourceCalibration /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsers /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
